import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchAccountContacts,
  fetchContactMethods,
  fetchContactList,
  fetchContactDetail,
  fetchContactTab,
  searchContacts,
  createContact,
  setPrimaryContact,
  type ContactListQuery,
  type ContactTabKey,
} from "@/api/contacts";
import type { ContactCreatePayload } from "@/api/types";

const STALE = 60_000;

/** Phase 3.X-1B sub_1 — global Contact LIST. */
export function useContactList(query: ContactListQuery) {
  return useQuery({
    queryKey: [
      "contactList",
      query.cursor ?? "",
      query.q ?? "",
      query.scope ?? "",
      query.manager ?? "",
      query.hasMethods ? 1 : 0,
      query.hasOpenTasks ? 1 : 0,
      query.needsReview ? 1 : 0,
      query.duplicateStatus ? 1 : 0,
    ],
    queryFn: ({ signal }) => fetchContactList(query, signal),
    staleTime: STALE,
  });
}

/** Phase 3.X-1B sub_2 — Contact DETAIL compact envelope. */
export function useContactDetail(contactId: string | undefined) {
  return useQuery({
    queryKey: ["contactDetail", contactId],
    queryFn: ({ signal }) => fetchContactDetail(contactId as string, signal),
    enabled: !!contactId,
    staleTime: STALE,
  });
}

/** Phase 3.X-1B sub_2 — lazy contact-detail tab (fetches only when active). */
export function useContactTab<T = Record<string, unknown>>(
  contactId: string | undefined,
  tab: ContactTabKey,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["contactTab", contactId, tab],
    queryFn: ({ signal }) => fetchContactTab<T>(contactId as string, tab, undefined, signal),
    enabled: !!contactId && enabled,
    staleTime: STALE,
  });
}

export function useAccountContacts(accountId: string | undefined, limit?: number) {
  return useQuery({
    queryKey: ["accountContacts", accountId, limit ?? null],
    queryFn: ({ signal }) => fetchAccountContacts(accountId!, limit, signal),
    enabled: !!accountId,
    staleTime: STALE,
  });
}

export function useContactMethods(
  contactId: string | undefined,
  accountContext?: string,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: ["contactMethods", contactId, accountContext ?? null],
    queryFn: ({ signal }) => fetchContactMethods(contactId!, accountContext, signal),
    enabled: !!contactId && enabled,
    staleTime: STALE,
  });
}

export function useContactSearch(
  q: string,
  contextType: "account" | "holding" | "outlet" | undefined,
  contextId: string | undefined,
) {
  return useQuery({
    queryKey: ["contactSearch", q, contextType ?? null, contextId ?? null],
    queryFn: ({ signal }) => searchContacts(q, contextType, contextId, signal),
    enabled: q.trim().length >= 2,
    staleTime: 30_000,
  });
}

export function useCreateContact(accountId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ContactCreatePayload) => createContact(payload),
    onSuccess: (res) => {
      if (res.status === "success") {
        if (accountId) {
          qc.invalidateQueries({ queryKey: ["accountContacts", accountId] });
          qc.invalidateQueries({ queryKey: ["accountOverview", accountId] });
        }
      }
    },
  });
}

export function useSetPrimaryContact(accountId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (contactId: string) => setPrimaryContact(accountId!, contactId),
    onSuccess: () => {
      if (accountId) {
        qc.invalidateQueries({ queryKey: ["accountContacts", accountId] });
        qc.invalidateQueries({ queryKey: ["accountOverview", accountId] });
      }
    },
  });
}
